import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToastStore } from "@/store/toastStore";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  TextField,
} from "@mui/material";
import {
  Edit,
  RemoveCircleOutline,
  ArrowUpward,
  ArrowDownward,
  Add,
} from "@mui/icons-material";
import {
  Button,
  Card,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  PageHeader,
  FiltersCard,
} from "@/components/ui";
import { productService, type Product, type Weekday } from "@/services/product.service";

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  STARTER: "Entrée",
  MAIN_COURSE: "Plat",
  DESSERT: "Dessert",
  DRINK: "Boisson",
  SIDE: "100 % Légumes",
};

const WEEKDAY_LABELS: Record<string, string> = {
  MONDAY: "Lun",
  TUESDAY: "Mar",
  WEDNESDAY: "Mer",
  THURSDAY: "Jeu",
  FRIDAY: "Ven",
  SATURDAY: "Sam",
  SUNDAY: "Dim",
};

const ALL_WEEKDAYS: Weekday[] = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

/**
 * Page de gestion des produits récurrents
 * Bowls, tartes salées, soupes créoles, etc. — produits proposés en continu sur le menu
 */
export function RecurrentProductsPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastStore();

  const [addProduct, setAddProduct] = useState<Product | null>(null);

  const { data: recurrentProducts = [], isLoading, error: queryError } = useQuery({
    queryKey: ["products", "recurrent"],
    queryFn: () => productService.getAll({ isRecurrent: true, availability: "all" }),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const { data: allProducts = [] } = useQuery({
    queryKey: ["products", "all"],
    queryFn: () => productService.getAll({ availability: "all" }),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const nonRecurrentProducts = allProducts.filter((p) => !p.isRecurrent);

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { isRecurrent?: boolean; recurrentDays?: Weekday[]; displayOrder?: number } }) =>
      productService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      success("Produit mis à jour");
      setAddProduct(null);
    },
    onError: (err: unknown) => {
      showError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          "Erreur lors de la mise à jour",
      );
    },
  });

  const sortedRecurrent = [...recurrentProducts].sort((a, b) => a.displayOrder - b.displayOrder);

  const handleRemoveRecurrent = (product: Product) => {
    updateMutation.mutate({ id: product.id, data: { isRecurrent: false, recurrentDays: [] } });
  };

  const handleAddRecurrent = () => {
    if (!addProduct) return;
    updateMutation.mutate({ id: addProduct.id, data: { isRecurrent: true, recurrentDays: [...ALL_WEEKDAYS] } });
  };

  const handleMoveUp = async (product: Product, index: number) => {
    if (index <= 0) return;
    const prev = sortedRecurrent[index - 1];
    try {
      await productService.update(product.id, { displayOrder: prev.displayOrder });
      await productService.update(prev.id, { displayOrder: product.displayOrder });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      success("Ordre mis à jour");
    } catch (err) {
      showError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          "Erreur lors du réordonnancement",
      );
    }
  };

  const handleMoveDown = async (product: Product, index: number) => {
    if (index >= sortedRecurrent.length - 1) return;
    const next = sortedRecurrent[index + 1];
    try {
      await productService.update(product.id, { displayOrder: next.displayOrder });
      await productService.update(next.id, { displayOrder: product.displayOrder });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      success("Ordre mis à jour");
    } catch (err) {
      showError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          "Erreur lors du réordonnancement",
      );
    }
  };

  const formatPrice = (p: number | string) =>
    typeof p === "number" ? p.toFixed(2) : Number(p).toFixed(2);

  if (isLoading) {
    return <LoadingSpinner message="Chargement des produits récurrents..." />;
  }

  if (queryError) {
    return <ErrorMessage message="Erreur lors du chargement des produits récurrents." />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Produits récurrents"
        subtitle="Bowls, tartes salées, soupes créoles, etc. — produits proposés en continu sur le menu de la semaine"
      />

      {/* Ajouter un produit récurrent */}
      {nonRecurrentProducts.length > 0 && (
        <FiltersCard>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Ajouter un produit à la liste des récurrents
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Autocomplete
              value={addProduct}
              onChange={(_, v) => setAddProduct(v)}
              options={nonRecurrentProducts}
              getOptionLabel={(opt) => opt.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField {...params} label="Produit" size="small" placeholder="Choisir un produit" />
              )}
              sx={{ minWidth: 280 }}
            />
            <Button
              variant="outline"
              startIcon={<Add />}
              onClick={handleAddRecurrent}
              disabled={!addProduct || updateMutation.isPending}
            >
              Ajouter comme récurrent
            </Button>
          </Box>
        </FiltersCard>
      )}

      {/* Liste des produits récurrents */}
      {sortedRecurrent.length === 0 ? (
        <EmptyState
          title="Aucun produit récurrent"
          description="Les produits récurrents (Bowls, tartes salées, soupes créoles, desserts du jour, etc.) apparaissent dans une section dédiée du menu. Ajoutez-en depuis la liste ci-dessus ou en cochant « Produit récurrent » dans la fiche produit."
          action={{
            label: "Voir tous les produits",
            onClick: () => navigate("/products"),
          }}
        />
      ) : (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={48} />
                  <TableCell>Ordre</TableCell>
                  <TableCell>Nom</TableCell>
                  <TableCell>Catégorie</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Jours</TableCell>
                  <TableCell align="right">Prix</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRecurrent.map((product, index) => (
                  <TableRow key={product.id} hover>
                    <TableCell padding="checkbox">
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveUp(product, index)}
                          disabled={index === 0}
                          aria-label="Monter"
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveDown(product, index)}
                          disabled={index === sortedRecurrent.length - 1}
                          aria-label="Descendre"
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>{product.displayOrder}</TableCell>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>{product.category?.name ?? "—"}</TableCell>
                    <TableCell>{PRODUCT_TYPE_LABELS[product.type] ?? product.type}</TableCell>
                    <TableCell>
                      {product.recurrentDays?.length
                        ? product.recurrentDays.map((d) => WEEKDAY_LABELS[d] ?? d).join(", ")
                        : "Tous les jours"}
                    </TableCell>
                    <TableCell align="right">{formatPrice(product.price)} €</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/products/${product.id}/edit`)}
                        aria-label="Modifier"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveRecurrent(product)}
                        disabled={updateMutation.isPending}
                        aria-label="Retirer des récurrents"
                      >
                        <RemoveCircleOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
}
